import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ThumbsUp } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import {
    fetchReviewSummary,
    fetchReviews,
    setReviewHelpful,
    Review,
    ReviewSummary,
} from '@/services/reviewsApi'
import { getColorwaySwatch, getInitial } from './reviewColorways'
import ReviewStars from './ReviewStars'
import ReviewPhotoLightbox from './ReviewPhotoLightbox'
import CustomerSay from './CustomerSay'
import CustomerSayThemeModal, { ThemeSnippet } from './CustomerSayThemeModal'
import { customerSay } from './customerSayData'
import { capDisplayRating } from '@/utils/ratingDisplay'

interface ProductReviewsProps {
    productId: string
    region?: string
    className?: string
    variant?: 'page' | 'modal'
}

const PAGE_SIZE = 6
const HELPFUL_VOTES_KEY = 'kibu_review_helpful_votes'

const loadVotedSet = (): Set<string> => {
    try {
        const raw = localStorage.getItem(HELPFUL_VOTES_KEY)
        return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>()
    } catch {
        return new Set<string>()
    }
}

const persistVotedSet = (votes: Set<string>) => {
    try {
        localStorage.setItem(HELPFUL_VOTES_KEY, JSON.stringify([...votes]))
    } catch {
        /* ignore quota/availability errors */
    }
}

const relativeDate = (iso: string | null): string => {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    return formatDistanceToNow(date, { addSuffix: true })
}

const STAR_ROWS: Array<'5' | '4' | '3' | '2' | '1'> = ['5', '4', '3', '2', '1']

const ProductReviews: React.FC<ProductReviewsProps> = ({ productId, region, className = '', variant = 'page' }) => {
    const [summary, setSummary] = useState<ReviewSummary | null>(null)
    const [reviews, setReviews] = useState<Review[]>([])
    const [lightbox, setLightbox] = useState<{ photos: string[]; index: number; caption?: string } | null>(null)
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)
    const [votedIds, setVotedIds] = useState<Set<string>>(() => loadVotedSet())
    const pendingVotes = useRef<Set<string>>(new Set())
    // "Customers say" chip → opens a theme panel of representative review snippets.
    // The full review set is fetched once (lazily) so the panel can draw from all of
    // them, not just the current page.
    const [activeTheme, setActiveTheme] = useState<string | null>(null)
    const [allReviews, setAllReviews] = useState<Review[] | null>(null)
    const [loadingAll, setLoadingAll] = useState(false)

    const selectTheme = useCallback(async (label: string | null) => {
        setActiveTheme(label)
        if (!label) return
        if (!allReviews && !loadingAll) {
            setLoadingAll(true)
            try {
                const data = await fetchReviews(productId, { sort: 'most_helpful', limit: 30 }, region)
                setAllReviews(data.items)
            } catch {
                /* fall back to the already-loaded page */
            } finally {
                setLoadingAll(false)
            }
        }
    }, [productId, region, allReviews, loadingAll])

    const activeCategory = useMemo(
        () => (activeTheme ? customerSay.categories.find((c) => c.label === activeTheme) ?? null : null),
        [activeTheme],
    )

    // A few representative snippets (highlights, not an exhaustive list) for the panel.
    const themeSnippets = useMemo<ThemeSnippet[]>(() => {
        if (!activeCategory) return []
        const source = allReviews ?? reviews
        const keywords = activeCategory.keywords.map((k) => k.toLowerCase())
        return source
            .filter((r) => {
                const text = `${r.title || ''} ${r.body || ''}`.toLowerCase()
                return keywords.some((k) => text.includes(k))
            })
            .slice(0, 4)
            .map((r) => ({ id: r.id, authorName: r.authorName, text: [r.title, r.body].filter(Boolean).join(' — ') }))
    }, [activeCategory, allReviews, reviews])

    useEffect(() => {
        let active = true
        setLoading(true)
        setFailed(false)
        Promise.all([
            fetchReviewSummary(productId, region),
            fetchReviews(productId, { sort: 'most_helpful', limit: PAGE_SIZE }, region),
        ])
            .then(([summaryData, listData]) => {
                if (!active) return
                setSummary(summaryData)
                setReviews(listData.items)
                setNextCursor(listData.nextCursor)
                setTotal(listData.total)
            })
            .catch(() => {
                if (active) setFailed(true)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [productId, region])

    const loadMore = useCallback(async () => {
        if (!nextCursor || loadingMore) return
        setLoadingMore(true)
        try {
            const data = await fetchReviews(
                productId,
                { sort: 'most_helpful', limit: PAGE_SIZE, cursor: nextCursor },
                region,
            )
            // Defensive dedupe so a stray server-side overlap can never render the
            // same review twice.
            setReviews((prev) => {
                const seen = new Set(prev.map((r) => r.id))
                return [...prev, ...data.items.filter((r) => !seen.has(r.id))]
            })
            setNextCursor(data.nextCursor)
            setTotal(data.total)
        } catch {
            /* keep what we have; button stays available to retry */
        } finally {
            setLoadingMore(false)
        }
    }, [productId, region, nextCursor, loadingMore])

    const toggleHelpful = useCallback(async (review: Review) => {
        if (pendingVotes.current.has(review.id)) return
        pendingVotes.current.add(review.id)

        const hasVoted = votedIds.has(review.id)
        const nextVoted = !hasVoted

        // Optimistic update.
        setVotedIds((prev) => {
            const next = new Set(prev)
            if (nextVoted) next.add(review.id)
            else next.delete(review.id)
            persistVotedSet(next)
            return next
        })
        setReviews((prev) => prev.map((r) => (
            r.id === review.id
                ? { ...r, helpfulCount: Math.max(0, r.helpfulCount + (nextVoted ? 1 : -1)) }
                : r
        )))

        try {
            const result = await setReviewHelpful(review.id, nextVoted)
            // Reconcile with the server's authoritative count.
            setReviews((prev) => prev.map((r) => (
                r.id === review.id ? { ...r, helpfulCount: result.helpfulCount } : r
            )))
        } catch {
            // Roll back the optimistic change.
            setVotedIds((prev) => {
                const next = new Set(prev)
                if (nextVoted) next.delete(review.id)
                else next.add(review.id)
                persistVotedSet(next)
                return next
            })
            setReviews((prev) => prev.map((r) => (
                r.id === review.id
                    ? { ...r, helpfulCount: Math.max(0, r.helpfulCount + (nextVoted ? -1 : 1)) }
                    : r
            )))
        } finally {
            pendingVotes.current.delete(review.id)
        }
    }, [votedIds])

    const totalLabel = useMemo(() => total.toLocaleString(), [total])
    const hasMore = Boolean(nextCursor)

    // Hide the whole block on error or when there are genuinely no reviews.
    if (!loading && (failed || total === 0)) {
        return null
    }

    const isModal = variant === 'modal'
    const sectionClass = isModal
        ? 'mx-auto w-full max-w-[960px] px-1.5 md:px-4'
        : 'module_container mx-auto w-full max-w-[960px] px-4'
    const cardClass = isModal
        ? `bg-white rounded-[18px] md:rounded-[30px] p-3.5 md:p-10 shadow-none ${className}`
        : `bg-white rounded-[28px] md:rounded-[30px] p-6 md:p-12 shadow-[0_4px_12px_rgba(0,0,0,0.10)] ${className}`
    const summaryStickyClass = isModal ? 'md:top-4' : 'md:top-28'

    if (loading) {
        return (
            <section className={sectionClass}>
                <div className={cardClass} aria-busy="true">
                    <div className="animate-pulse">
                        <div className="h-3 w-20 rounded bg-[#EEE] mb-3" />
                        <div className="h-7 w-72 max-w-full rounded bg-[#EEE] mb-8" />
                        <div className="grid gap-8 md:grid-cols-[240px_1fr]">
                            <div className="space-y-3">
                                <div className="h-14 w-24 rounded bg-[#EEE]" />
                                <div className="h-4 w-40 rounded bg-[#EEE]" />
                                {STAR_ROWS.map((s) => <div key={s} className="h-2 w-full rounded-full bg-[#EEE]" />)}
                            </div>
                            <div className="space-y-6">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="space-y-2 border-t border-[#E9E9E9] pt-5">
                                        <div className="h-4 w-32 rounded bg-[#EEE]" />
                                        <div className="h-4 w-48 rounded bg-[#EEE]" />
                                        <div className="h-4 w-full rounded bg-[#EEE]" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    const average = capDisplayRating(summary?.average ?? 0)
    const distribution = summary?.distribution

    return (
        <section id="product-full-reviews" className={sectionClass}>
            <div className={cardClass}>
                {!isModal && (
                    <CustomerSay className="hidden md:block mb-7 pb-7 border-b border-[#E9E9E9]" activeTheme={activeTheme} onSelectTheme={selectTheme} />
                )}
                <div className="font-headline text-xs uppercase tracking-[0.08em] text-orange mb-1.5">Reviews</div>
                <h2 className="font-headline text-[22px] md:text-[28px] leading-[1.1] tracking-[-0.02em] text-black mb-5 md:mb-[30px]">
                    What families are saying
                </h2>

                <div className="grid gap-7 md:grid-cols-[240px_1fr] md:gap-12 md:items-start">
                    {/* ===== Summary (score/stars; sticky on desktop) ===== */}
                    <div className={`md:sticky ${summaryStickyClass} md:self-start`}>
                        {/* Desktop summary (vertical, with % labels + build-again line) */}
                        <div className="hidden md:block">
                            <div className="flex items-end gap-2">
                                <span className="font-headline text-[64px] leading-[0.9] text-black">{average}</span>
                                <span className="font-headline text-[18px] text-[#8A8A8A] pb-2">/5</span>
                            </div>
                            <ReviewStars rating={average} size={18} className="block mt-2.5 mb-1.5" />
                            <div className="text-[13px] text-[#6B6B6B] mb-[22px]">Based on {totalLabel} reviews</div>
                            {distribution && STAR_ROWS.map((stars) => {
                                const pct = Math.round((distribution[stars] || 0) * 100)
                                return (
                                    <div key={stars} className="flex items-center gap-2.5 mb-[9px]">
                                        <span className="font-headline text-[13px] text-[#3D3D3D] w-2.5 text-right">{stars}</span>
                                        <span className="text-orange text-[11px]">★</span>
                                        <div className="flex-1 h-2 rounded-full bg-[#E9E9E9] overflow-hidden">
                                            <div className="h-full bg-orange" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-[12px] text-[#6B6B6B] w-[30px] text-right">{pct}%</span>
                                    </div>
                                )
                            })}
                            {typeof summary?.wouldBuildAgainPct === 'number' && (
                                <div className="mt-6 pt-5 border-t border-[#E9E9E9] text-[13px] leading-[1.5] text-green">
                                    {summary.wouldBuildAgainPct}% would build a second pair.
                                </div>
                            )}
                        </div>

                        {/* Mobile summary (horizontal panel, compact bars) */}
                        <div className="md:hidden">
                            <div className="flex items-center gap-[18px] bg-[#F4F4F4] rounded-[20px] p-[18px] mb-2">
                                <div className="text-center flex-none">
                                    <div className="font-headline text-[44px] leading-[0.9] text-black">{average}</div>
                                    <ReviewStars rating={average} size={14} className="block mt-1.5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    {distribution && STAR_ROWS.map((stars) => {
                                        const pct = Math.round((distribution[stars] || 0) * 100)
                                        return (
                                            <div key={stars} className="flex items-center gap-2 mb-1.5 last:mb-0">
                                                <span className="text-[12px] text-[#3D3D3D] w-2">{stars}</span>
                                                <div className="flex-1 h-1.5 rounded-full bg-[#E0DDD6] overflow-hidden">
                                                    <div className="h-full bg-orange" style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="text-[12px] text-[#8A8A8A] mb-1.5">Based on {totalLabel} reviews</div>
                            {typeof summary?.wouldBuildAgainPct === 'number' && (
                                <div className="text-[12px] leading-[1.5] text-green">{summary.wouldBuildAgainPct}% would build a second pair.</div>
                            )}
                        </div>
                    </div>

                    {/* ===== List ===== */}
                    <div>
                        {reviews.map((review) => {
                            const swatch = getColorwaySwatch(review.colorway, review.id)
                            const voted = votedIds.has(review.id)
                            const meta = [review.colorway, relativeDate(review.createdAt)].filter(Boolean).join(' · ')
                            return (
                                <div key={review.id} className="py-[22px] border-t border-[#E9E9E9]">
                                    <div className="flex items-center gap-3 mb-2.5">
                                        <div
                                            className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-headline text-sm md:text-base flex-none"
                                            style={{ background: swatch.bg, color: swatch.text }}
                                        >
                                            {getInitial(review.authorName)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-headline text-[14px] md:text-[15px] text-black">{review.authorName}</span>
                                                {review.verifiedBuild && (
                                                    <span className="hidden md:inline text-[11px] text-kibuBlue bg-[#EBEDFA] px-2.5 py-0.5 rounded-full">Verified build</span>
                                                )}
                                            </div>
                                            {meta && <div className="text-[12px] text-[#8A8A8A] mt-0.5">{meta}</div>}
                                        </div>
                                        {typeof review.rating === 'number' && <ReviewStars rating={review.rating} size={14} />}
                                    </div>
                                    {review.title && (
                                        <div className="font-headline text-[15px] md:text-[16px] text-black mb-1.5">{review.title}</div>
                                    )}
                                    {review.body && (
                                        <div className="font-body text-[14px] leading-[1.55] text-[#3D3D3D]">{review.body}</div>
                                    )}
                                    {review.photos && review.photos.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {review.photos.map((photo, photoIndex) => (
                                                <button
                                                    key={photo}
                                                    type="button"
                                                    onClick={() => setLightbox({ photos: review.photos, index: photoIndex, caption: review.authorName })}
                                                    className="block rounded-xl overflow-hidden transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-orange"
                                                    aria-label="View review photo"
                                                >
                                                    <img
                                                        src={photo}
                                                        alt="Customer review"
                                                        loading="lazy"
                                                        className="w-20 h-20 md:w-24 md:h-24 object-cover"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {review.reply && (
                                        <div className="mt-3 rounded-2xl bg-[#F4F4F4] p-3.5">
                                            <div className="font-headline text-[12px] text-black mb-1">Response from Kibu</div>
                                            <div className="font-body text-[13px] leading-[1.5] text-[#3D3D3D]">{review.reply.text}</div>
                                        </div>
                                    )}
                                    <div className="hidden md:flex items-center gap-2.5 mt-3.5">
                                        <span className="text-[12px] text-[#8A8A8A]">Was this helpful?</span>
                                        <button
                                            type="button"
                                            onClick={() => toggleHelpful(review)}
                                            aria-pressed={voted}
                                            className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-[12px] transition-colors duration-200 ${
                                                voted ? 'border-orange text-orange' : 'border-[#E9E9E9] text-[#6B6B6B] hover:border-[#C9C9C9]'
                                            }`}
                                        >
                                            <ThumbsUp className="w-[13px] h-[13px]" fill={voted ? 'currentColor' : 'none'} />
                                            {review.helpfulCount}
                                        </button>
                                    </div>
                                </div>
                            )
                        })}

                        {hasMore && (
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="mt-2 w-full py-3 rounded-full bg-white border-2 border-black font-headline text-[14px] text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
                            >
                                {loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {lightbox && (
                <ReviewPhotoLightbox
                    photos={lightbox.photos}
                    startIndex={lightbox.index}
                    caption={lightbox.caption}
                    onClose={() => setLightbox(null)}
                />
            )}
            {activeCategory && (
                <CustomerSayThemeModal
                    label={activeCategory.label}
                    count={activeCategory.count}
                    blurb={activeCategory.blurb}
                    keywords={activeCategory.keywords}
                    snippets={themeSnippets}
                    loading={loadingAll}
                    onClose={() => selectTheme(null)}
                />
            )}
        </section>
    )
}

export default ProductReviews
